import { useId, useMemo, useRef, useState } from "react";
import "./CreateTicketForm.css";
import {
  createTicketAPI,
  updateTicketAPI,
} from "../../../utils/api/teamTicket";
import { Button, Card, Checkbox, Input, NoticeBox, Select } from "../../ui";

const TICKET_TEMPLATES = {
  "قائمة الطعام": ["جديد", "مراجعة", "اضافة"],
  "تسجيل في التطبيقات": ["جاهز", "مرسول", "هنقر", "كيتا", "نينجا", "تويو"],
  "تسجيل الربط": ["تسجيل المتجر في الربط"],
};

const WEEKDAYS = {
  ar: ["الاحد", "الاثنين", "الثلثاء", "الاربعاء", "الخميس", "الجمعة", "السبت"],
  en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
};

const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

const EMPTY_FORM = {
  title: "",
  description: "",
  storeId: "",
  assignedTo: "",
  message: "",
  schedule: { isRecurring: false, interval: 1, dayOfMonth: [] },
};

// The parent keys this form by the ticket being edited, so the initial state
// is built once per ticket instead of being synced in an effect.
const buildInitialForm = (isUpdate, initialData) => {
  if (!isUpdate || !initialData) return EMPTY_FORM;
  return {
    title: initialData?.title || "",
    description: initialData?.description || "",
    storeId: initialData.storeId?._id || "",
    assignedTo: initialData.assignedTo?._id || "",
    message: "",
    schedule: {
      isRecurring: initialData?.scheduled?.isRecurring || false,
      frequency: initialData?.scheduled?.frequency || "",
      interval: initialData?.scheduled?.interval || 1,
      timeOfDayMinutes: initialData?.scheduled?.timeOfDayMinutes ?? undefined,
      daysOfWeek: initialData?.scheduled?.daysOfWeek || [],
      dayOfMonth: initialData?.scheduled?.dayOfMonth || [],
    },
  };
};

const minutesToTime = (minutes) =>
  minutes === undefined
    ? ""
    : `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(
        minutes % 60,
      ).padStart(2, "0")}`;

// Returns the labels of the required fields that are still missing.
const getMissingFields = (form) => {
  const missing = [];
  if (!form.title) missing.push("Title");
  if (!form.description) missing.push("Description");
  if (!form.storeId) missing.push("Store");
  if (!form.assignedTo) missing.push("Assignee");

  const { isRecurring, frequency, interval, timeOfDayMinutes, daysOfWeek, dayOfMonth } =
    form.schedule;
  if (isRecurring) {
    if (!frequency) missing.push("Frequency");
    if (!Number.isInteger(interval) || interval < 1) missing.push("Interval");
    if (timeOfDayMinutes === undefined) missing.push("Time");
    if (frequency === "weekly" && !daysOfWeek?.length) missing.push("Days of week");
    if (frequency === "monthly" && !(Array.isArray(dayOfMonth) && dayOfMonth.length))
      missing.push("Days of month");
  }
  return missing;
};

export default function CreateTicketForm({
  users,
  stores,
  onClose,
  onCreated,
  lang,
  handleTokenExpiration,
  isUpdate = false,
  initialData = null,
}) {
  const [form, setForm] = useState(() => buildInitialForm(isUpdate, initialData));
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const submittingRef = useRef(false);
  const missingId = useId();

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setSchedule = (key, value) =>
    setForm((prev) => ({ ...prev, schedule: { ...prev.schedule, [key]: value } }));

  const templateOptions = useMemo(
    () => [
      { value: "", label: "اختر نوع الطلب" },
      ...Object.keys(TICKET_TEMPLATES).map((title) => ({
        value: title,
        label: title,
      })),
    ],
    [],
  );

  const descriptionOptions = useMemo(
    () => [
      { value: "", label: "اختر الوصف" },
      ...(TICKET_TEMPLATES[form.title] || []).map((desc) => ({
        value: desc,
        label: desc,
      })),
    ],
    [form.title],
  );

  const storeOptions = useMemo(
    () => [
      { value: "", label: "Select Store" },
      ...stores.map((store) => ({
        value: store._id,
        label: store.businessName,
      })),
    ],
    [stores],
  );

  const userOptions = useMemo(
    () => [
      { value: "", label: "Assign To" },
      ...users.map((user) => ({
        value: user._id,
        label: user.name,
      })),
    ],
    [users],
  );

  const frequencyOptions = useMemo(
    () => [
      { value: "", label: "Select Frequency" },
      { value: "daily", label: lang === "ar" ? "يومي" : "Daily" },
      { value: "weekly", label: lang === "ar" ? "أسبوعي" : "Weekly" },
      { value: "monthly", label: lang === "ar" ? "شهري" : "Monthly" },
    ],
    [lang],
  );

  const missingFields = getMissingFields(form);
  const isEnabled = missingFields.length === 0;

  const toggleDay = (key, day, checked) =>
    setForm((prev) => {
      const days = new Set(prev.schedule[key] || []);
      if (checked) days.add(day);
      else days.delete(day);
      return { ...prev, schedule: { ...prev.schedule, [key]: Array.from(days) } };
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEnabled || submittingRef.current) return;

    const token = localStorage.getItem("auth-token");
    if (!token) return handleTokenExpiration();

    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError("");

    // The API helpers resolve to the saved ticket, or undefined on failure.
    const saved = isUpdate
      ? await updateTicketAPI(token, initialData._id, form)
      : await createTicketAPI(token, form);

    submittingRef.current = false;
    if (!saved) {
      // Keep the form open with everything the user entered.
      setSubmitting(false);
      setSubmitError(
        isUpdate
          ? "The ticket could not be updated. Please try again."
          : "The ticket could not be created. Please try again.",
      );
      return;
    }

    onClose();
    onCreated();
  };

  const weekdayLabels = WEEKDAYS[lang === "ar" ? "ar" : "en"];

  return (
    <Card hoverable={false} className="ticket-form-card">
      <form onSubmit={handleSubmit} className="ticket-form" noValidate>
        <h2 className="ticket-form-title">
          {isUpdate ? "Update Ticket" : "Create Ticket"}
        </h2>

        <Select
          label="Request type"
          className="ticket-input"
          value={form.title}
          options={templateOptions}
          placeholder="اختر نوع الطلب"
          onChange={(e) =>
            setForm((prev) => ({ ...prev, title: e.target.value, description: "" }))
          }
        />

        <Input
          label="Title"
          placeholder="Title"
          className="ticket-input"
          value={form.title}
          onChange={(e) => setField("title", e.target.value)}
          required
        />

        {form.title && Object.keys(TICKET_TEMPLATES).includes(form.title) && (
          <Select
            label="Description template"
            className="ticket-input"
            value={form.description}
            options={descriptionOptions}
            placeholder="اختر الوصف"
            onChange={(e) => setField("description", e.target.value)}
          />
        )}

        <Input
          textarea
          label="Description"
          placeholder="Description"
          className="ticket-input"
          value={form.description}
          onChange={(e) => setField("description", e.target.value)}
          required
        />

        <div className="ticket-form-row">
          <Select
            label="Store *"
            className="ticket-input"
            value={form.storeId}
            options={storeOptions}
            placeholder="Select Store"
            onChange={(e) => setField("storeId", e.target.value)}
          />

          <Select
            label="Assign to *"
            className="ticket-input"
            value={form.assignedTo}
            options={userOptions}
            placeholder="Assign To"
            onChange={(e) => setField("assignedTo", e.target.value)}
          />
        </div>

        <Input
          textarea
          label="First comment"
          placeholder="First Comment"
          className="ticket-input"
          value={form.message}
          onChange={(e) => setField("message", e.target.value)}
        />

        <Checkbox
          checked={form.schedule.isRecurring}
          onChange={(e) => setSchedule("isRecurring", e.target.checked)}
          label={lang === "ar" ? "هل هو مجدول" : "Is Scheduled"}
        />

        {/* ===== Schedule Inputs (only if isRecurring) ===== */}
        {form.schedule.isRecurring && (
          <div className="schedule-block">
            <Select
              label={
                lang === "ar"
                  ? "تكرار يومي، أسبوعي، شهري؟"
                  : "Frequency (daily, weekly, monthly)"
              }
              className="ticket-input"
              value={form.schedule.frequency || ""}
              options={frequencyOptions}
              placeholder="Select Frequency"
              onChange={(e) => setSchedule("frequency", e.target.value)}
            />

            <Input
              label={lang === "ar" ? "كل عدد من المرات " : "Interval"}
              type="number"
              min={1}
              step={1}
              placeholder="Interval (e.g., every N days/weeks/months)"
              className="ticket-input"
              value={form.schedule.interval}
              onChange={(e) => setSchedule("interval", Number(e.target.value))}
              required
            />

            <Input
              label={lang === "ar" ? "الوقت" : "Time"}
              type="time"
              className="ticket-input"
              value={minutesToTime(form.schedule.timeOfDayMinutes)}
              onChange={(e) => {
                if (!e.target.value) {
                  // A cleared time field would otherwise become NaN.
                  setSchedule("timeOfDayMinutes", undefined);
                  return;
                }
                const [hours, minutes] = e.target.value.split(":").map(Number);
                setSchedule("timeOfDayMinutes", hours * 60 + minutes);
              }}
              required
            />

            {form.schedule.frequency === "weekly" && (
              <fieldset className="weekdays-block">
                <legend>{lang === "ar" ? "أيام الأسبوع" : "Days of week"}</legend>
                {weekdayLabels.map((label, d) => (
                  <Checkbox
                    key={d}
                    checked={form.schedule.daysOfWeek?.includes(d) || false}
                    onChange={(e) => toggleDay("daysOfWeek", d, e.target.checked)}
                    label={label}
                  />
                ))}
              </fieldset>
            )}

            {form.schedule.frequency === "monthly" && (
              <fieldset className="weekdays-block month-days">
                <legend>{lang === "ar" ? "أيام الشهر" : "Days of month"}</legend>
                {MONTH_DAYS.map((day) => (
                  <Checkbox
                    key={day}
                    checked={form.schedule.dayOfMonth?.includes(day) || false}
                    onChange={(e) => toggleDay("dayOfMonth", day, e.target.checked)}
                    label={String(day)}
                  />
                ))}
              </fieldset>
            )}
          </div>
        )}

        {submitError && (
          <div role="alert">
            <NoticeBox variant="danger" title={submitError} />
          </div>
        )}

        {!isEnabled && (
          <p className="ticket-form-missing" id={missingId}>
            Required: {missingFields.join(", ")}
          </p>
        )}

        <div className="ticket-form-actions">
          <Button
            type="submit"
            variant={isEnabled ? "success" : "neutral"}
            appearance={isEnabled ? "filled" : "soft"}
            className="ticket-button"
            disabled={!isEnabled}
            loading={submitting}
            aria-describedby={isEnabled ? undefined : missingId}
            aria-label={submitting ? "Saving…" : undefined}
            fullWidth
          >
            {isUpdate ? "Update Ticket" : "Create Ticket"}
          </Button>
        </div>
      </form>
    </Card>
  );
}
